import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PageHero } from '../components/PageHero';
import { CtaBand } from '../components/CtaBand';
import { Reveal } from '../components/Reveal';
import { usePageTitle } from '../hooks/usePageTitle';
import { ASSESSMENT_URL } from '../config/foundation';

export function Programmes() {
  usePageTitle('Programmes');
  return (
    <Layout>
      <PageHero eyebrow="Our Flagship Programme" title={<>LifeSpark360&#8482;</>}>
        The programme through which the Foundation delivers its mission — helping people build
        skills, confidence, and independence. With free and funded places, and a Seedbed
        programme for those starting out.
      </PageHero>

      {/* LifeSpark360 Detail */}
      <section className="py-16 sm:py-24">
        <div className="section">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <Reveal>
              <p className="eyebrow mb-3">Flagship Programme</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-mustard-green-dark mb-4 tracking-display">
                LifeSpark360&#8482;
              </h2>
              <p className="text-slate-500 leading-relaxed mb-6">
                A comprehensive 12-week coaching programme for people who are ready to take control of
                their direction in work and life. Built on the SPARK Method — a proven five-phase
                framework that takes you from self-reflection to sustained momentum.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                <InfoItem label="Duration" value="12 weeks" />
                <InfoItem label="Sessions" value="12 weekly" />
                <InfoItem label="Format" value="1-to-1 coaching" />
                <InfoItem label="Method" value="SPARK (5 phases)" />
                <InfoItem label="Resources" value="20+ documents" />
                <InfoItem label="Cost" value="Free / funded places" />
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/apply" className="btn-primary text-center">Apply for a Place</Link>
                <a href={ASSESSMENT_URL} className="btn-secondary text-center">Check Eligibility</a>
              </div>
            </Reveal>
            <Reveal delay={120} className="space-y-4">
              <h3 className="font-display text-lg font-bold text-mustard-green-dark mb-4">The SPARK Journey</h3>
              <PhaseDetail
                letter="S" title="See Yourself Clearly" sessions="Sessions 1-2"
                items={['Life Story Mapping exercise', 'Values Identification workbook', 'Four Foundations Assessment', 'Pre-work pack and coaching agreement']}
              />
              <PhaseDetail
                letter="P" title="Plant New Roots" sessions="Sessions 3-4"
                items={['Identity workbooks', 'Three Guides introduction', 'SPARK Journal setup', 'Foundation-building exercises']}
              />
              <PhaseDetail
                letter="A" title="Arm Yourself" sessions="Sessions 5-7"
                items={['CV Master Template and transformation', 'LinkedIn profile overhaul guide', 'ACHIEVE Story Bank builder', 'Job Search Tracker and strategy', 'THRIVE Six Review framework']}
              />
              <PhaseDetail
                letter="R" title="Rise to the Room" sessions="Sessions 8-10"
                items={['Mock interview preparation', 'THRIVE Six deep-dive review', 'Leadership presence building', 'Confidence and interview techniques']}
              />
              <PhaseDetail
                letter="K" title="Keep the Flame" sessions="Sessions 11-12"
                items={['90-Day Launch Plan', 'Completion certificate', 'Branded celebration materials', 'Ongoing momentum frameworks']}
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Session Structure */}
      <section className="bg-white py-16 sm:py-24 border-y border-slate-100">
        <div className="section">
          <Reveal className="text-center mb-12">
            <p className="eyebrow mb-3">Each Session</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-display">
              <span className="text-gradient">The CRBC Session Model</span>
            </h2>
            <p className="text-slate-500 mt-3 max-w-lg mx-auto">Every coaching session follows a consistent 60-minute structure.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { time: '10 min', title: 'Check-In', desc: 'Energy score, micro-action review, and opening reflection.', color: 'bg-mustard-green-dark' },
              { time: '15 min', title: 'Reflect', desc: 'Biggest insight from the last session. Deep exploration of progress.', color: 'bg-mustard-green' },
              { time: '25 min', title: 'Build', desc: 'Core coaching work. New frameworks, exercises, and skill development.', color: 'bg-mustard-green-mid' },
              { time: '10 min', title: 'Commit', desc: 'Three micro-actions set. Next session prep. Accountability anchored.', color: 'bg-mustard-gold' },
            ].map((b, i) => (
              <Reveal key={b.title} delay={i * 100}>
                <SessionBlock {...b} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Seedbed */}
      <section className="py-16 sm:py-24">
        <div className="section">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <Reveal className="lg:order-2">
              <p className="eyebrow mb-3">Foundation Programme</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-mustard-green-dark mb-4 tracking-display">
                Seedbed
              </h2>
              <p className="text-slate-500 leading-relaxed mb-6">
                An 8-week exploration programme for those who need to build foundational clarity before
                committing to the full LifeSpark360 journey. Ideal for career changers, returners,
                or anyone at a crossroads who wants structured guidance.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                <InfoItem label="Duration" value="8 weeks" />
                <InfoItem label="Sessions" value="4 fortnightly" />
                <InfoItem label="Format" value="1-to-1 coaching" />
                <InfoItem label="Phases" value="4 (S-E1-E2-D)" />
                <InfoItem label="Cost" value="Free / funded places" />
                <InfoItem label="Pathway" value="Feeds into LS360" />
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/apply" className="btn-primary text-center">Apply for a Place</Link>
                <Link to="/discovery" className="btn-secondary text-center">Start Discovery Tool</Link>
              </div>
            </Reveal>
            <Reveal delay={120} className="lg:order-1 space-y-4">
              <h3 className="font-display text-lg font-bold text-mustard-green-dark mb-4">Seedbed Phases</h3>
              <PhaseDetail
                letter="S" title="Start" sessions="Session 1"
                items={['Welcome and orientation', 'Initial exploration exercises', 'Goal-setting framework', 'Coaching agreement']}
              />
              <PhaseDetail
                letter="E1" title="Explore 1" sessions="Session 2"
                items={['Skills and strengths audit', 'Values alignment check', 'Career landscape mapping']}
              />
              <PhaseDetail
                letter="E2" title="Explore 2" sessions="Session 3"
                items={['Workbook exercises', 'Direction testing', 'Barrier identification and strategies']}
              />
              <PhaseDetail
                letter="D" title="Decide" sessions="Session 4"
                items={['Decision framework', 'Next steps plan', 'LifeSpark360 pathway review', 'Completion badge']}
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="bg-white py-16 sm:py-24 border-y border-slate-100">
        <div className="section">
          <Reveal className="text-center mb-12">
            <p className="eyebrow mb-3">Included</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-display">
              <span className="text-gradient">What every programme includes</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'Certified LifeSpark360 coach',
              'Pre-session questionnaire system',
              'Micro-action tracking between sessions',
              'Staged document delivery portal',
              'Self-directed workbooks',
              'AI-powered CV assessment',
              'Readiness assessment profile',
              'Community discussion board',
              'Completion certificate',
            ].map((item, i) => (
              <Reveal key={item} delay={(i % 3) * 90}>
                <div className="flex items-center gap-3 p-4 rounded-xl border border-slate-100 bg-mustard-cream/40 hover:border-mustard-green-mid/40 transition-colors">
                  <div className="w-5 h-5 rounded-full bg-mustard-green-dark/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-mustard-green-dark" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                  </div>
                  <span className="text-sm text-mustard-charcoal font-medium">{item}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        title="Find out which programme is right for you"
        subtitle="Take the free 41-question readiness assessment. Your score determines whether LifeSpark360 or Seedbed is the right starting point."
      >
        <a href={ASSESSMENT_URL} className="btn-gold !py-3.5 !px-8">Start Free Assessment</a>
      </CtaBand>
    </Layout>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">{label}</p>
      <p className="text-sm font-semibold text-mustard-charcoal mt-0.5">{value}</p>
    </div>
  );
}

function PhaseDetail({ letter, title, sessions, items }: { letter: string; title: string; sessions: string; items: string[] }) {
  return (
    <div className="flex gap-4 p-4 rounded-xl border border-slate-100 hover:border-mustard-green-mid/30 hover:shadow-card transition-all">
      <div className="w-10 h-10 rounded-xl bg-brand-gradient text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-mustard-green-dark/15">
        <span className="font-display text-lg font-bold">{letter}</span>
      </div>
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <h4 className="font-display font-bold text-mustard-green-dark text-sm">{title}</h4>
          <span className="text-[11px] text-mustard-gold font-semibold">{sessions}</span>
        </div>
        <ul className="mt-1.5 space-y-0.5">
          {items.map((item, i) => (
            <li key={i} className="text-xs text-slate-500 flex items-start gap-1.5">
              <span className="text-mustard-green-mid mt-0.5">&#8226;</span>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function SessionBlock({ time, title, desc, color }: { time: string; title: string; desc: string; color: string }) {
  return (
    <div className="card h-full p-6 text-center">
      <div className={`w-12 h-12 ${color} text-white rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-black/5`}>
        <span className="text-xs font-bold">{time}</span>
      </div>
      <h3 className="font-display font-bold text-mustard-green-dark mb-1">{title}</h3>
      <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
    </div>
  );
}
